import { createServerFn } from "@tanstack/react-start";

import { CONTENT_TTL_SECONDS } from "./cachePolicy";
import { readStamp, stillCurrent } from "./contentGeneration";
import { isCmsPreview } from "./preview";
import { contentSource } from "./source";
import type { AcademyArticle, AcademyHub } from "./types";

/**
 * What the academy routes call. Server functions rather than plain loader
 * fetches, for the same reason the blog's are: a route loader re-runs in the
 * browser on client-side navigation, which would put a CMS token in the
 * client bundle.
 */
const CACHE_TTL_MS = CONTENT_TTL_SECONDS * 1000;

let cache:
  | {
      loadedAt: number;
      generation: number;
      articles: Promise<AcademyArticle[]>;
    }
  | undefined;

async function loadArticles(): Promise<AcademyArticle[]> {
  const { loadAcademyArticles } = await contentSource();
  return loadAcademyArticles();
}

function cachedArticles(): Promise<AcademyArticle[]> {
  // A preview request reads the drafts, fresh, and holds nothing: the copy
  // every other request shares is the published one and stays so.
  if (isCmsPreview()) {
    return loadArticles();
  }
  if (!stillCurrent(cache, CACHE_TTL_MS)) {
    const articles = loadArticles().catch((error: unknown) => {
      cache = undefined;
      throw error;
    });
    cache = { ...readStamp(), articles };
  }
  return cache.articles;
}

export const getAcademyArticle = createServerFn({ method: "GET" })
  .inputValidator((slug: unknown): string => String(slug ?? ""))
  .handler(
    async ({ data }): Promise<AcademyArticle | null> =>
      (await cachedArticles()).find((article) => article.slug === data) ?? null
  );

let hubCache:
  | {
      loadedAt: number;
      generation: number;
      hubs: Map<string, Promise<AcademyHub | null>>;
    }
  | undefined;

/**
 * Hubs are cached per path rather than as one list: fourteen of them share one
 * article set, and loading that set once per TTL is what the blog's cache does
 * for the same reason.
 */
export const getAcademyHub = createServerFn({ method: "GET" })
  .inputValidator((path: unknown): string => String(path ?? ""))
  .handler(async ({ data }): Promise<AcademyHub | null> => {
    // A preview request reads the draft, fresh, and holds nothing.
    if (isCmsPreview()) {
      return (await contentSource()).loadAcademyHub(data);
    }
    return cachedHub(data);
  });

/**
 * The hub at a path, loaded once per TTL and shared by every request in it. A
 * publish ends the copy early (`contentGeneration.ts`).
 */
function cachedHub(path: string): Promise<AcademyHub | null> {
  if (!stillCurrent(hubCache, CACHE_TTL_MS)) {
    hubCache = { ...readStamp(), hubs: new Map() };
  }
  let hub = hubCache.hubs.get(path);
  if (!hub) {
    hub = contentSource().then(({ loadAcademyHub }) => loadAcademyHub(path));
    hubCache.hubs.set(path, hub);
  }
  return hub;
}
